/**
 * inflationBasis — the ONE place the platform's inflation assumption and the
 * nominal↔real conversion live.
 *
 * WHY THIS EXISTS. Two engines needed the same number for different reasons and
 * had arrived at it separately: `finDecision/pathwayLcoe` exported a
 * `DEFAULT_INFLATION_PCT` of 2.0 to deflate a nominal discount rate, and the
 * project-finance engine escalated O&M at 2.0%/yr. Those two 2.0s are not a
 * coincidence — they are the same assumption, and the real-vs-nominal identity
 * below only holds while they stay equal:
 *
 *   escalating a cost at `i` and discounting at `r_nominal`
 *     ≡ holding that cost flat and discounting at `r_real`
 *
 * Two independently-maintained constants would let that identity drift silently,
 * and the failure mode is not a crash — it is a real LCOE and a nominal LCOE
 * that no longer describe the same project.
 */

/**
 * Long-run general inflation, percent per year.
 *
 * 2.0%/yr — the US Federal Reserve's stated longer-run objective for PCE
 * inflation ("Statement on Longer-Run Goals and Monetary Policy Strategy",
 * reaffirmed annually). NREL's Annual Technology Baseline holds O&M constant in
 * REAL terms, which is the same assumption seen from the other side. This is a
 * documented default, not a tuned figure; callers may override per jurisdiction
 * or scenario.
 */
export const DEFAULT_INFLATION_PCT_PER_YEAR = 2.0;

/**
 * Fisher equation — convert a NOMINAL discount rate to a REAL one.
 *
 *   1 + r_real = (1 + r_nominal) / (1 + i)
 *
 * Note this is the exact form, not the `r − i` approximation, which drifts by
 * roughly `r × i` — around 15 basis points at an 8% WACC and 2% inflation, and
 * enough to move a levelised cost over a long asset life.
 *
 * Rates in, rate out, all as FRACTIONS (0.075 = 7.5%); `inflationPct` is a
 * PERCENT (2.0 = 2%), matching how the assumption is stored and displayed.
 */
export function realDiscountRateFraction(
  nominalRateFraction: number,
  inflationPct: number = DEFAULT_INFLATION_PCT_PER_YEAR,
): number {
  const inflation = inflationPct / 100;
  if (Math.abs(inflation) < 1e-12) return nominalRateFraction;
  return (1 + nominalRateFraction) / (1 + inflation) - 1;
}

/**
 * Natural-gas fuel price escalation, percent per year (nominal).
 *
 * 3.0%/yr — EIA Annual Energy Outlook 2024 reference-case Henry Hub trajectory,
 * the same figure the benchmark pathway presets already price gas at
 * (`GAS_CC_BENCHMARKS` / `GAS_SC_BENCHMARKS`, sourced to NREL ATB 2024 / EIA AEO
 * 2024). This module owns it so the FA engine and the pathway presets cannot
 * drift apart — the two are printed side by side in the same report.
 *
 * Above general inflation by design: gas is a traded commodity whose price is
 * not tied to CPI, and the AEO reference case has it outpacing inflation over
 * the outlook period. Escalating fuel at CPI instead materially understates the
 * lifetime cost of a gas plant, whose opex is dominated by fuel.
 */
export const DEFAULT_GAS_FUEL_PRICE_CAGR_PCT = 3.0;

/** Technologies whose fuel is a traded commodity with its own price path. */
type FuelEscalationTechnology =
  | 'smr' | 'nuclear-existing' | 'solar-bess' | 'wind-bess'
  | 'storage' | 'gas-cc' | 'gas-sc';

/**
 * The nominal escalation rate for a technology's FUEL line, percent per year.
 *
 * Gas burns a commodity and gets the AEO path. Everything else gets general
 * inflation, which means its fuel is held constant in REAL terms:
 *
 *  - solar / wind / storage declare no fuel at all, so the rate is moot;
 *  - nuclear fuel is a small, largely contracted cost (fabrication and
 *    enrichment services rather than spot uranium), and holding it flat in real
 *    terms is the conservative reading. Note the benchmark presets specify NO
 *    CAGR for nuclear, i.e. flat NOMINAL — which would erode the fuel cost to
 *    near nothing in real terms across a sixty-year life. Real-flat is the more
 *    defensible of the two, and the difference is small because nuclear fuel is
 *    a few percent of LCOE.
 */
export function fuelEscalatorPctPerYear(
  technology: FuelEscalationTechnology | string | null | undefined,
): number {
  return technology === 'gas-cc' || technology === 'gas-sc'
    ? DEFAULT_GAS_FUEL_PRICE_CAGR_PCT
    : DEFAULT_INFLATION_PCT_PER_YEAR;
}

/**
 * Convert a NOMINAL escalation rate to its REAL counterpart — the same Fisher
 * relation as {@link realDiscountRateFraction}, applied to a growth rate:
 *
 *   1 + g_real = (1 + g_nominal) / (1 + i)
 *
 * Needed because a real-basis LCOE holds costs flat only where they escalate AT
 * inflation. Gas fuel escalates ABOVE it, so in real terms it still grows —
 * roughly 0.98%/yr at 3% nominal and 2% inflation. Treating it as flat would
 * understate the real LCOE of a gas plant.
 */
export function realEscalatorFraction(
  nominalEscalatorFraction: number,
  inflationPct: number = DEFAULT_INFLATION_PCT_PER_YEAR,
): number {
  const inflation = inflationPct / 100;
  if (Math.abs(inflation) < 1e-12) return nominalEscalatorFraction;
  return (1 + nominalEscalatorFraction) / (1 + inflation) - 1;
}
